import { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { connect, useSelector } from 'react-redux';
import _ from 'lodash';
import { PhotoIcon, FileIcon, UserIcon } from '../../../../components/Icon';
import AutoCompleteSelect from '../../../../components/AutoCompleteSelect';
import { i18n } from '../../../../language';
import { ehandoverAPI } from '../../../../apis';
import lengthVerification from '../../../../utils/lengthVerification';
import { WarnAlert } from '../CreateNote';
import ShowSelectedFiles from '../ShowSelectedFiles';
import ehandoverIds from '../../../../ids/Ehandover';
import { revokePreviewUrl } from '../../../../utils';
import { DragDropUpload } from '../../../../components';
import useUploadFile, { ACCEPT_FILE_TYPES } from '../../../../utils/hooks/useUploadFile';

const useStyles = makeStyles((theme) => ({
  root: {
    border: '0.0625rem solid #e4e7eb',
    borderRadius: '0.9375rem',
  },
  textarea: {
    width: '100%',
    borderRadius: '0.9375rem',
    height: '9rem',
    border: 'none',
    outline: 'none',
    resize: 'none',
    padding: '1.25rem 1.875rem 0',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.main,
    fontWeight: '500',
  },
  icons: {
    padding: '0 1.813rem 1.438rem 0',
    borderBottom: '0.0625rem solid #e4e7eb',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  photo: {
    width: '1.25rem',
    height: '1.125rem',
    color: '#5f9df6',
    marginRight: '1.438rem',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  file: {
    width: '1.125rem',
    height: '1.188rem',
    color: '#5f9df6',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  foot: {
    padding: '0 1.875rem 0 1.688rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '5.5rem',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
  },
  user: {
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
    marginRight: '0.938rem',
  },
  select: {
    width: '10.625rem',
    height: '2.5rem',
    padding: '0.5rem 0.687rem 0.5rem 0.625rem',
    borderRadius: '0.5rem',
    boxShadow: '-0.0625rem 0.25rem 0.625rem rgba(221, 232, 251, 0.58)',
    border: '0.0625rem solid #ddd',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
  },
  Cancel: {
    borderRadius: '0.625rem',
    border: '0.09375rem solid #538dff',
    backgroundColor: '#eff6ff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    fontWeight: '500',
    color: theme.palette.primary.main,
    width: '9.375rem',
    height: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '1.25rem',
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: '#eff6ff',
    },
  },
  Submit: {
    borderRadius: '0.5rem',
    backgroundColor: '#4c93ff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    lineHeight: '1.25rem',
    color: theme.palette.font.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '9.375rem',
    height: '3rem',
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: '#4c93ff',
    },
  },
  inputfile: {
    width: '0.1px',
    height: '0.1px',
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    zIndex: -1,
  },
  label: {},
}));

function AddComment({
  setControl,
  TASK_ID,
  getCommentAPI,
  updateCommentsCountAPI,
  wardStaffdata,
  context,
  setContext,
  id,
  commentNeedLogin,
  onHeightChange = () => {}
}) {
  const classes = useStyles();
  const loginInfo = useSelector((state) => state.loginInfo);
  const [corpId, setCorpId] = useState(loginInfo?.flag ? loginInfo.corpID.toUpperCase() : null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [warnFile, setWarnFile] = useState([]);
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnText, setWarnText] = useState(1);
  const inputRef = useRef(null);
  const [fileTypeWarn, setFileTypeWarn] = useState([]);
  const { selectFile, imgList, setImgList, fileList, setFileList } = useUploadFile();

  // create Comment API
  const postCreateComment = async ({ msg, taskfiles, id }) => {
    try {
      await ehandoverAPI.postCreateComment({
        fields: {
          authorId: id,
          files: _.map(taskfiles, (item) => {
            return {
              content: item.base64[0],
              name: item.name,
            };
          }),
          postMessage: msg,
        },
        taskId: TASK_ID,
      });
      getCommentAPI();
      updateCommentsCountAPI();
      handleReset();
    } catch (error) {
      console.log('-----Create Comment error----->', error);
    }
  };

  const handleChangeContext = (event) => {
    if (lengthVerification(event.target.value, 1001)) {
      return;
    }
    setContext(event.target.value);
  };

  const selectName = (data) => {
    setCorpId(data);
  };

  const handleReset = () => {
    setContext('');
    setControl();
    setImgList([]);
    setCorpId(null);
    setFileList([]);
    setAlertOpen(false);
    setWarnFile([]);
  };

  const handleAddComment = () => {
    if (context === 'undefined' || !context || !/[^\s]/.test(context)) {
      setWarnOpen(true);
      inputRef.current.focus();
      setWarnText(2);
    } else if (corpId === null) {
      setWarnOpen(true);
      setWarnText(1);
    } else {
      let taskfiles = [...imgList, ...fileList];
      // api返回的corpId有可能跟当前登录的corpId有大小写区别，所以需要找回api的里面的corpId
      const corpIdFromApi = _.find(wardStaffdata, (item) => item?.value === corpId)?.id;
      revokePreviewUrl(imgList);
      (async () => {
        await postCreateComment({
          msg: context,
          taskfiles,
          id: corpIdFromApi,
        });
      })();
    }
  };

  const handleDragDropUpload = async (files) => {
    await selectFile(files, setWarnFile, setAlertOpen, setFileTypeWarn);
  };

  useEffect(() => {
    if (!loginInfo?.flag) {
      setCorpId(null);
    } else {
      setCorpId(loginInfo.corpID.toUpperCase());
    }
  }, [loginInfo?.flag]);

  useEffect(() => {
    onHeightChange?.();
  },[imgList,
    fileList,])

  return (
    <DragDropUpload onUpload={handleDragDropUpload}>
      <div className={classes.root}>
        <WarnAlert
          text={
            fileTypeWarn.length > 0
              ? i18n.t('EHANDOVER_FILE_TYPE_WARN', {
                  text: ACCEPT_FILE_TYPES.map((v) => {
                    return `"${v.replace('.', '')}"`;
                  }).join(','),
                })
              : i18n.t('EHANDOVER_FILE_WARN', { value: warnFile.toString() })
          }
          open={alertOpen}
          onClose={() => {
            setAlertOpen(false);
            setWarnFile([]);
          }}
        />
        <input
          type="file"
          name="photo"
          id={`photo${TASK_ID}`}
          accept=".jpg,.png"
          capture="camera"
          onChange={(event) => {
            (async () => {
              await selectFile(event?.target?.files, setWarnFile, setAlertOpen, setFileTypeWarn);
            })();
          }}
          multiple
          className={classes.inputfile}
          onClick={(event) => {
            event.target.value = '';
          }}
        />
        <input
          type="file"
          name="file"
          id={`file${TASK_ID}`}
          accept=".jpg,.png,.mp4,.pdf,.docx,.pptx,.xlsx,.txt,.helc"
          onChange={(event) => {
            (async () => {
              await selectFile(event?.target?.files, setWarnFile, setAlertOpen, setFileTypeWarn);
            })();
          }}
          multiple
          className={classes.inputfile}
          onClick={(event) => {
            event.target.value = '';
          }}
        />
        <div className={classes.body}>
          <textarea
            id={ehandoverIds.addComment + id + 1}
            className={classes.textarea}
            value={context}
            ref={inputRef}
            placeholder={i18n.t('EHANDOVER_ADD_COMMENT_PLACEHOLDER')}
            onChange={handleChangeContext}
          />
          <ShowSelectedFiles photo={imgList} files={fileList} setPhoto={setImgList} setFiles={setFileList} />
          <div className={classes.icons}>
            <label id={ehandoverIds.addComment + id + 2} htmlFor={`photo${TASK_ID}`} className={classes.label}>
              <PhotoIcon className={classes.photo} />
            </label>
            <label id={ehandoverIds.addComment + id + 3} htmlFor={`file${TASK_ID}`} className={classes.label}>
              <FileIcon className={classes.file} />
            </label>
          </div>
        </div>
        <div className={classes.foot}>
          <WarnAlert
            open={warnOpen}
            onClose={() => setWarnOpen(false)}
            text={warnText === 1 ? i18n.t('EHANDOVER_COMMENT_WARN') : i18n.t('EHANDOVER_CREATE_COMMENT_WARN')}
          />
          <div className={classes.left}>
            <UserIcon className={classes.user} />
            <AutoCompleteSelect
              itemList={wardStaffdata}
              height="2.813rem"
              value={corpId}
              onChange={selectName}
              disabled={commentNeedLogin}
              placeholder={i18n.t('EHANDOVER_COMMENT_POSTBY')}
              id={id + ehandoverIds.addComment + 4}
            />
          </div>
          <div className={classes.left}>
            <Button
              id={ehandoverIds.addComment + id + 5}
              variant="outlined"
              className={classes.Cancel}
              onClick={() => {
                setContext('');
                setControl();
              }}
            >
              {i18n.t('EHANDOVER_COMMENT_CANCEL')}
            </Button>
            <Button
              id={ehandoverIds.addComment + id + 6}
              variant="outlined"
              className={classes.Submit}
              onClick={handleAddComment}
            >
              {i18n.t('EHANDOVER_COMMENT_SUBMIT')}
            </Button>
          </div>
        </div>
      </div>
    </DragDropUpload>
  );
}

const mapStateToProps = (state) => ({
  wardId: state.loginInfo.wardId,
});

export default connect(mapStateToProps)(AddComment);
